import { useMap } from "@vis.gl/react-maplibre";
import { ChangeEvent, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import { getAliveMap } from "@ryde/controls";
import { useGoogleServices } from "@ryde/redux";
import {
  canInsertStationRouteWaypoint,
  enrichStationRouteWaypointLocationDraft,
  IPlacePrediction,
  isCancelledSimpleResult,
  IStationRouteStationInfo,
  IStationRouteWaypointLocationDraft,
  SimpleResult,
  StationRouteWaypointAddMode,
  warnAndToastSimpleResult,
} from "@ryde/shared";

export interface IUseStationRouteWaypointMapControlsParams {
  stations: IStationRouteStationInfo[];
  routePolyline?: string;
  addWaypointFromDraft: (
    draft: IStationRouteWaypointLocationDraft,
  ) => Promise<SimpleResult<void>>;
}

export const useStationRouteWaypointMapControls = ({
  stations,
  routePolyline,
  addWaypointFromDraft,
}: IUseStationRouteWaypointMapControlsParams) => {
  const { current: mapRef } = useMap();
  const mapContainer = getAliveMap(mapRef)?.getContainer() ?? null;
  const { t } = useTranslation();
  const { getCoordinateByPlaceId } = useGoogleServices();
  const anchorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const popperContentRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [addMode, setAddMode] = useState<StationRouteWaypointAddMode>(
    StationRouteWaypointAddMode.None,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [addressDraft, setAddressDraft] = useState("");
  const [customNameDraft, setCustomNameDraft] = useState("");
  const [pendingDraft, setPendingDraft] = useState<
    IStationRouteWaypointLocationDraft | undefined
  >(undefined);
  const [addressOptions, setAddressOptions] = useState<IPlacePrediction[]>([]);

  const isPopperOpen =
    isMenuOpen ||
    addMode === StationRouteWaypointAddMode.Search ||
    Boolean(pendingDraft);

  const panMapTo = useEvent((latitude: number, longitude: number) => {
    getAliveMap(mapRef)?.easeTo({
      center: [longitude, latitude],
      duration: 300,
    });
  });

  const closeAll = useEvent(() => {
    setIsMenuOpen(false);
    setAddMode(StationRouteWaypointAddMode.None);
    setPendingDraft(undefined);
    setAddressDraft("");
    setCustomNameDraft("");
    setAddressOptions([]);
  });

  const handleClickAway = useEvent((event: MouseEvent | TouchEvent) => {
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }

    closeAll();
  });

  const handleTriggerClick = useEvent(() => {
    if (!canInsertStationRouteWaypoint(stations)) {
      return;
    }

    if (addMode === StationRouteWaypointAddMode.MapClick) {
      return;
    }

    if (
      isMenuOpen ||
      addMode === StationRouteWaypointAddMode.Search ||
      pendingDraft
    ) {
      closeAll();
      return;
    }

    setIsMenuOpen(true);
  });

  const startSearchMode = useEvent(() => {
    setIsMenuOpen(true);
    setAddMode(StationRouteWaypointAddMode.Search);
  });

  const startMapClickMode = useEvent(() => {
    setIsMenuOpen(false);
    setAddMode(StationRouteWaypointAddMode.MapClick);
    setPendingDraft(undefined);
    setAddressDraft("");
  });

  const handleLocationPicked = useEvent(
    (draft: IStationRouteWaypointLocationDraft) => {
      const enrichedDraft = enrichStationRouteWaypointLocationDraft({
        draft,
        stations,
        routePolyline,
      });
      setPendingDraft(enrichedDraft);
      setAddressDraft(enrichedDraft.fullAddress);
      setAddMode(StationRouteWaypointAddMode.Search);
      setIsMenuOpen(true);
      panMapTo(enrichedDraft.latitude, enrichedDraft.longitude);
    },
  );

  const handleAddressChange = useEvent(
    async (newValue: IPlacePrediction | null) => {
      if (!newValue) {
        setAddressDraft("");
        setPendingDraft(undefined);
        return;
      }

      const locationResult = await getCoordinateByPlaceId(newValue.placeId);
      const { success, data } = locationResult ?? {};

      if (!success || !data?.lat || !data.lng) {
        setPendingDraft(undefined);
        setAddressDraft("");
        warnAndToastSimpleResult(locationResult, t);
        return;
      }

      const draft = enrichStationRouteWaypointLocationDraft({
        draft: {
          fullAddress: newValue.text?.text ?? "",
          latitude: data.lat,
          longitude: data.lng,
        },
        stations,
        routePolyline,
      });
      setAddressDraft(draft.fullAddress);
      setPendingDraft(draft);
      panMapTo(draft.latitude, draft.longitude);
    },
  );

  const handleSelectInsertAfterIndex = useEvent((insertAfterIndex: number) => {
    setPendingDraft(prev => (prev ? { ...prev, insertAfterIndex } : prev));
  });

  const handleCustomNameChange = useEvent(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCustomNameDraft(event.target.value);
    },
  );

  const handleConfirm = useEvent(async () => {
    if (!pendingDraft?.latitude || !pendingDraft.longitude) {
      return;
    }

    setIsSaving(true);
    try {
      const draftToAdd: IStationRouteWaypointLocationDraft = {
        ...pendingDraft,
        fullAddress: addressDraft || pendingDraft.fullAddress,
        customName: customNameDraft.trim() || undefined,
      };
      const result = await addWaypointFromDraft(draftToAdd);

      if (isCancelledSimpleResult(result)) {
        return;
      }

      if (result.success) {
        closeAll();
        return;
      }

      warnAndToastSimpleResult(result, t);
    } finally {
      setIsSaving(false);
    }
  });

  const addressValue = useMemo(
    () =>
      addressDraft
        ? ({
            placeId: "",
            text: { text: addressDraft },
          } as IPlacePrediction)
        : null,
    [addressDraft],
  );

  const canAddWaypoint = canInsertStationRouteWaypoint(stations);
  const canConfirm = Boolean(
    pendingDraft?.latitude && pendingDraft?.longitude && !isSaving,
  );
  const isButtonActive =
    isMenuOpen || addMode !== StationRouteWaypointAddMode.None;
  const isSearchPanelOpen =
    addMode === StationRouteWaypointAddMode.Search || Boolean(pendingDraft);
  const isMapClickMode = addMode === StationRouteWaypointAddMode.MapClick;

  return {
    mapContainer,
    anchorRef,
    toolbarRef,
    popperContentRef,
    t,
    addressOptions,
    addressValue,
    canAddWaypoint,
    canConfirm,
    closeAll,
    customNameDraft,
    handleAddressChange,
    handleClickAway,
    handleConfirm,
    handleCustomNameChange,
    handleLocationPicked,
    handleSelectInsertAfterIndex,
    handleTriggerClick,
    isButtonActive,
    isMapClickMode,
    isPopperOpen,
    isSearchPanelOpen,
    pendingDraft,
    setAddressOptions,
    startMapClickMode,
    startSearchMode,
  };
};
